<template>
  <div class="dark-card mb-3">
    <div class="card-header">
      <v-icon color="#5cb860" small class="mr-2">mdi-bell-outline</v-icon>
      <span class="card-title-text">Notifications</span>
      <span class="header-spacer"></span>

      <!-- <span
        class="toggle-link"
        :title="maximized ? 'Minimize notifications' : 'Maximize notifications'"
        @click="toggleMaximized"
      >
        <v-icon small color="#5cb860" class="mr-1">{{ maximized ? "mdi-arrow-collapse-all" : "mdi-arrow-expand-all" }}</v-icon>
      </span> -->
      <span
        v-if="showRead && notifications.length > 0"
        class="toggle-link"
        title="Delete all notifications"
        @click="updateNotifications('delete', notifications.map((n) => n.id))"
      >
        <v-icon small color="#e53935" class="mr-1">{{
          "mdi-delete-forever"
        }}</v-icon>
      </span>

      <span

        class="toggle-link"
        :title="
          showRead ? 'Show only new notifications' : 'Show all notifications'
        "
        @click="toggleShowRead"
      >
        <v-icon small color="#5cb860" class="mr-1">{{
          showRead ? "mdi-email-outline" : "mdi-email-open-outline"
        }}</v-icon>
      </span>

      <span class="header-divider"></span>

      <span
        class="toggle-link"
        title="Dismiss all notifications"
        v-if="notifications.filter((n) => n.status !== 'read').length > 0"
        @click="
          updateNotifications(
            'read',
            notifications.filter((n) => n.status !== 'read').map((n) => n.id)
          )
        "
      >
        <v-icon small color="#5cb860" class="mr-1">{{
          "mdi-check-all"
        }}</v-icon>
      </span>

      <span
        class="toggle-link"
        title="Refresh notifications"
        @click="loadNotifications"
      >
        <v-icon
          small
          color="#5cb860"
          class="mr-1"
          :class="{ 'is-spinning': loading }"
          >{{ "mdi-refresh" }}</v-icon
        >
      </span>
    </div>

    <div class="notifications-body">
      <div
        v-for="notification in notifications"
        :key="notification.id"
        class="notification-item"
        :class="{ 'is-read': notification.status === 'read' }"
      >
        <div
          class="notification-content"
          :title="
            notification.actionUrl && notification.status !== 'read'
              ? 'Click to open link and mark as read'
              : !notification.actionUrl && notification.status !== 'read'
              ? 'Click to mark as read'
              : notification.actionUrl && notification.status === 'read'
              ? 'Click to open link'
              : ''
          "
          @click="
            notification.status !== 'read' &&
              updateNotifications('read', [notification.id]),
              openUrl(notification)
          "
        >
          <div class="notification-title">{{ notification.title }}</div>
          <div class="notification-description">
            {{ notification.description }}
          </div>
        </div>

        <div class="notification-actions">
          <div class="notification-date" :title="notification.createdAt">
            {{ formatCreatedAt(notification.createdAt) }}
          </div>
          <!-- <v-icon
            v-if="notification.actionUrl"
            small
            color="#1976d2"
            title="Open link"
            @click.stop="openUrl(notification)"
            >mdi-open-in-new</v-icon
          > -->
          <div class="notification-icons">
            <v-icon
              v-if="notification.status !== 'read'"
              small
              color="#5cb860"
              title="Dismiss"
              @click.stop="updateNotifications('read', [notification.id])"
              >mdi-check</v-icon
            >
            <v-icon
              v-if="notification.status === 'read'"
              small
              color="#5cb860"
              title="Restore"
              @click.stop="updateNotifications('none', [notification.id])"
              >mdi-restore</v-icon
            >
            <v-icon
              v-if="notification.status == 'read'"
              small
              color="#e53935"
              title="Delete"
              @click.stop="updateNotifications('delete', [notification.id])"
              >mdi-delete</v-icon
            >
          </div>
        </div>
      </div>

      <div
        v-if="!loading && notifications.length === 0"
        class="notifications-empty"
      >
        <v-icon color="#bdbdbd" class="mb-1">mdi-bell-sleep-outline</v-icon>
        <div>
          {{ showRead ? "No read notifications" : "No new notifications" }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import firebase from "firebase/app";
import "firebase/database";
import router from "@/router";
import store from "@/store";

let messageQuery = null;
let messageRetryTimer = null;

function startMessageNotifications() {
  clearTimeout(messageRetryTimer);
  messageRetryTimer = null;
  if (messageQuery) {
    messageQuery.off();
    messageQuery = null;
  }

  const currentUser = store.state.authentication.user;
  if (!currentUser) return;

  if (!firebase.apps.length) {
    messageRetryTimer = setTimeout(startMessageNotifications, 1000);
    return;
  }

  const processedKeys = new Set();
  let ready = false;

  messageQuery = firebase
    .database()
    .ref("notification-messages")
    .orderByChild("User")
    .equalTo(currentUser.toLowerCase())
    .limitToLast(100);

  messageQuery.once("value", (snapshot) => {
    if (snapshot.exists()) {
      Object.keys(snapshot.val()).forEach((key) => processedKeys.add(key));
    }
    ready = true;
  });

  messageQuery.on("child_added", (snapshot) => {
    if (!ready || processedKeys.has(snapshot.key)) return;
    processedKeys.add(snapshot.key);
    showMessagePopup(snapshot.val() || {});
    window.dispatchEvent(new CustomEvent("notification-message-created"));
  });
}

store.watch((state) => state.authentication.user, startMessageNotifications);

function openNotificationActionUrl(actionUrl) {
  if (!actionUrl) return;
  try {
    const parsed = new URL(actionUrl, window.location.origin);
    if (parsed.origin !== window.location.origin) {
      window.open(actionUrl, "_blank");
      return;
    }
    const path = parsed.pathname + parsed.search + parsed.hash;
    router.push(path || "/").catch(() => {});
  } catch (e) {
    window.open(actionUrl, "_blank");
  }
}

function showMessagePopup(notification) {
  const title = notification.Title || "New Notification";
  let body = notification.Description || "";
  if (body.length > 180) body = body.substring(0, 177) + "...";

  if (notification.PushRealtime) {
    const notify = () =>
      router.app &&
      router.app.$notify({
        group: "notification",
        title: "New Notification",
        text: title,
        duration: 15000,
        type: "info",
      });

    if (!("Notification" in window) || Notification.permission !== "granted") {
      notify();
      return;
    }

    try {
      const browserNotification = new Notification("New Notification", {
        body: title,
        icon: "https://utaw.blob.core.windows.net/images/logo+uta11.png",
        tag: "notification-message-" + (notification.Id || Date.now()),
      });
      browserNotification.onclick = (event) => {
        if (event && event.preventDefault) {
          event.preventDefault();
        }
        window.focus();
        openNotificationActionUrl(notification.ActionUrl);
        browserNotification.close();
      };
    } catch (err) {
      notify();
    }
  }
}

export default {
  name: "NotificationsPanel",
  data() {
    return {
      notifications: [],
      loading: false,
      showRead: false,
      maximized: false,
    };
  },
  created() {
    this.loadNotifications();
    startMessageNotifications();
    this.onNotificationMessageCreated = () => {
      console.log("onNotificationMessageCreated");
      this.loadNotifications();
    };
    window.addEventListener(
      "notification-message-created",
      this.onNotificationMessageCreated
    );
  },
  beforeDestroy() {
    window.removeEventListener(
      "notification-message-created",
      this.onNotificationMessageCreated
    );
  },
  methods: {
    toggleMaximized() {
      this.maximized = !this.maximized;
    },
    formatCreatedAt(value) {
      if (!value) return "";
      return moment(value).format("MM/DD/YYYY h:mm A");
    },
    async loadNotifications() {
      this.loading = true;
      try {
        const res = await this.$http.get(
          "api/NotificationMessages/GetNotifications",
          { params: { onlyPending: !this.showRead, channel: "Web" } }
        );
        var messages = (res && res.messages) || [];
        messages.sort((a, b) => {
          var aTime = new Date(a.createdAt).getTime();
          var bTime = new Date(b.createdAt).getTime();
          if (aTime !== bTime) {
            return bTime - aTime;
          }
          return (a.id || 0) - (b.id || 0);
        });
        this.notifications = messages;
      } catch (e) {
        this.notifications = [];
      } finally {
        this.loading = false;
      }
    },
    async updateNotifications(action, ids) {
      if (!ids.length) {
        return;
      }
      try {
        await this.$http.post("api/NotificationMessages/UpdateNotifications", {
          Ids: ids,
          Action: action,
        });
        await this.loadNotifications();
      } catch (e) {
        // error already surfaced by http interceptor
      }
    },
    toggleShowRead() {
      this.showRead = !this.showRead;
      this.loadNotifications();
    },
    openUrl(notification) {
      if (notification.actionUrl) {
        window.open(notification.actionUrl, "_blank");
      }
    },
  },
};
</script>

<style scoped>
.dark-card {
  background: #fff;
  border-radius: 4px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08);
}
.mb-3 {
  margin-bottom: 10px !important;
}
.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}
.card-title-text {
  font-size: 13px;
  font-weight: 500;
  color: #616161;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.header-spacer {
  flex: 1;
}
.header-divider {
  width: 1px;
  height: 16px;
  margin: 0 8px 0 4px;
  background: #e0e0e0;
}
.toggle-link {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 500;
  color: #5cb860;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.is-spinning {
  animation: notification-spin 0.8s linear infinite;
}
@keyframes notification-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.notifications-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 260px;
  overflow-y: auto;
}
.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  transition: background 0.15s;
}
.notification-item:hover {
  background: #e3f2fd;
}
.notification-item.is-read {
  opacity: 0.65;
}
.notification-content {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}
.notification-title {
  font-size: 13px;
  font-weight: 600;
  color: #424242;
}
.notification-description {
  font-size: 12px;
  color: #757575;
  margin-top: 2px;
}
.notification-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
}
.notification-date {
  font-size: 11px;
  color: #585353;
  white-space: nowrap;
}
.notification-icons {
  display: flex;
  align-items: center;
  gap: 4px;
}
.notification-actions .v-icon {
  cursor: pointer;
}
.notifications-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  color: #9e9e9e;
  font-size: 13px;
}
</style>
